import { useMemo } from "react";
import cn from "classnames";
import { SeaReferenceTypesModel } from "entities/shared/seaCard.types";
import { ShipmentType } from "entities/shared/shipmentCard.types";
import { ShipmentTypeChecker } from "entities/shared/utils";
import { Nullable, ValueOf } from "shared/lib/typeCheck.lib";
import { CarrierIcon } from "shared/ui/CarrierIcon";
import { ShipmentTypeAirIcon, TrainIcon } from "shared/ui/Icons/Icons";
import { Tooltip } from "shared/ui/Tooltip";
import {
  createAirlineIconUrl,
  createParcelCarrierIconUrl,
  createScacIconUrl,
  createSealineIconUrl,
} from "shared/utils/url.utils";
import { CardRow } from "./CardRow";
import { CardStatus } from "./CardStatus";
import { ContainerTooltip } from "./ContainerTooltip";
import s from "../Card.module.scss";

import { CardHeaderStatus } from "../Card";
import { useCardContext } from "../Card.context";

export type CardHeaderProps = {
  shipmentType: ValueOf<typeof ShipmentType>;
  status?: CardHeaderStatus | null;
  reference?: Nullable<string>;
  referenceType?: ValueOf<typeof SeaReferenceTypesModel> | string | null;
  containersAmount?: number;
  carrierName?: string | null;
  scac?: string | null;
  className?: string;
  containersSizeTypes?: string[];
  pieces?: Nullable<string>;
  tags?: string[];
  count?: number;
};

export const CardHeader: RFC<CardHeaderProps> = ({
  shipmentType,
  referenceType,
  reference,
  containersAmount,
  className,
  status,
  containersSizeTypes,
  scac,
  carrierName,
  pieces,
  tags,
  count,
}) => {
  const { onUpdate, canUpdate, isUpdating } = useCardContext();
  const hasContainers = typeof containersAmount !== "undefined" && containersAmount > 0;
  const hasTags = tags && tags.length > 0;

  const shouldRenderPieces = !!pieces && ShipmentTypeChecker.isAir(shipmentType);
  const shouldRenderContainerTooltip = hasContainers && containersSizeTypes && ShipmentTypeChecker.isSea(shipmentType);
  const shouldRenderReferenceType = referenceType && ShipmentTypeChecker.isSea(shipmentType);
  const shouldRenderCount = !!count && ShipmentTypeChecker.isRail(shipmentType);

  const scacIconUrl = useMemo(() => {
    if (!scac) return undefined;
    switch (shipmentType) {
      case ShipmentType.SEA:
        return createSealineIconUrl(scac);

      case ShipmentType.AIR:
        return createAirlineIconUrl(scac);

      case ShipmentType.PARCEL:
        return createParcelCarrierIconUrl(scac);
      default:
        return createScacIconUrl(scac);
    }
  }, [scac, shipmentType]);

  return (
    <CardRow className={cn(s.header, className)}>
      <Tooltip
        triggerElement={
          <div className={s.tooltipWrap}>
            {scac && scacIconUrl && (
              <CarrierIcon src={scacIconUrl} label={carrierName ?? ""} code={scac} className={s.scacImg} size={24} />
            )}
          </div>
        }
        offsetY={10}
        offsetX={20}
        enabledOnMobile
      >
        <p className={s.tooltipCarrierName}>{carrierName}</p>
      </Tooltip>

      {shouldRenderReferenceType && (
        <div className={s.referenceType} data-test-id="card-reference-type">
          {referenceType === "CONTAINER" ? "CT" : referenceType}
        </div>
      )}

      {reference && (
        <div className={cn(s.containerId, s.rightShift)} data-test-id="card-number">
          {reference}
        </div>
      )}

      {shouldRenderPieces && (
        <div className={s.pieces}>
          <ShipmentTypeAirIcon className={s.piecesIcon} /> {pieces}
        </div>
      )}

      {shouldRenderCount && (
        <div className={s.countBox}>
          <TrainIcon className={s.countIcon} />
          <span className={s.count}>{count}</span>
        </div>
      )}

      <div className={s.updateContainer}>
        {shouldRenderContainerTooltip && (
          <ContainerTooltip containersAmount={containersAmount} containersSizeTypes={containersSizeTypes} />
        )}
        {hasTags && (
          <Tooltip
            triggerElement={<div className={cn(s.tagsWrap, s[shipmentType.toLowerCase()])}># {tags.length}</div>}
            offsetY={10}
            enabledOnMobile
          >
            <p className={s.tooltipTagsName}>{tags.join(", ")}</p>
          </Tooltip>
        )}
        <CardStatus
          shipmentType={shipmentType}
          onUpdate={onUpdate}
          canUpdate={canUpdate}
          isUpdating={isUpdating}
          status={status}
          className={cn(s.containerStatus, s.isClickable)}
        />
      </div>
    </CardRow>
  );
};

CardHeader.displayName = "CardHeader";
